// React-facing board store: the paged document + tool state. Low frequency only.
// Anything that changes at 60fps (active stroke, view matrix, drag overlay) lives in liveStore.

import { create } from 'zustand';
// the .ts extension keeps this loadable by node --test, which does not resolve the `@` alias
import {
  createPage,
  idSet,
  translateStroke,
  translateText,
  type BoardPage,
  type Stroke,
  type TextObject,
  type ToolId,
} from '../../whiteboard/model.ts';

export const PEN_COLORS = ['#1f2937', '#2563eb', '#dc2626', '#16a34a', '#f59e0b'];
export const PEN_SIZES = [4, 9, 18];

const HISTORY_LIMIT = 50;

/** Undo snapshot of one page's content. Ids are stable, so this is a shallow copy. */
interface PageContent {
  strokes: Stroke[];
  texts: TextObject[];
}

interface PageHistory {
  past: PageContent[];
  future: PageContent[];
}

export interface BoardState {
  pages: BoardPage[];
  pageIndex: number;
  /** Fixed for imported documents such as PDFs; null keeps the blank board appendable. */
  pageLimit: number | null;
  /** Parallel to `pages` - undo history is per page, as the frame's page control implies. */
  histories: PageHistory[];
  /** Bumped on every document mutation and on page switch - the renderer detects a stale bake. */
  revision: number;
  /** Ids selected by the marquee, on the current page only. */
  selection: string[];

  tool: ToolId;
  color: string;
  size: number;

  setTool: (tool: ToolId) => void;
  setColor: (color: string) => void;
  setSize: (size: number) => void;

  addStroke: (stroke: Stroke) => void;
  addText: (text: TextObject) => void;
  replaceText: (text: TextObject) => void;
  /** Removes a stroke or a text block by id - the eraser does not care which. */
  eraseObject: (id: string) => void;

  setSelection: (ids: string[]) => void;
  clearSelection: () => void;
  deleteSelection: () => void;
  moveSelection: (dx: number, dy: number) => void;

  clearPage: () => void;
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (index: number) => void;
  loadPages: (pages: BoardPage[], pageIndex?: number) => void;
  configurePageCount: (count: number | null) => void;

  undo: () => void;
  redo: () => void;
}

function pushHistory(past: PageContent[], content: PageContent): PageContent[] {
  const next = past.concat([content]);
  return next.length > HISTORY_LIMIT ? next.slice(next.length - HISTORY_LIMIT) : next;
}

function emptyHistory(): PageHistory {
  return { past: [], future: [] };
}

/** Immutable edit of the current page plus one history entry. Returns a `set` patch. */
function editPage(
  s: BoardState,
  strokes: Stroke[],
  texts: TextObject[],
  selection: string[],
): Partial<BoardState> {
  const i = s.pageIndex;
  const prev = s.pages[i];
  const pages = s.pages.slice();
  pages[i] = { id: prev.id, strokes, texts };
  const histories = s.histories.slice();
  histories[i] = {
    past: pushHistory(s.histories[i].past, { strokes: prev.strokes, texts: prev.texts }),
    future: [],
  };
  return { pages, histories, selection, revision: s.revision + 1 };
}

/** The page the tools act on. Use this everywhere instead of reaching into `pages`. */
export function currentPage(s: BoardState): BoardPage {
  return s.pages[s.pageIndex];
}

export const useBoardStore = create<BoardState>((set, get) => ({
  pages: [createPage()],
  pageIndex: 0,
  pageLimit: null,
  histories: [emptyHistory()],
  revision: 0,
  selection: [],

  tool: 'hand',
  color: PEN_COLORS[0],
  size: PEN_SIZES[1],

  // Changing tool always drops the selection - a marquee must not survive into the pen.
  setTool: (tool) => set({ tool, selection: [], revision: get().revision + 1 }),
  setColor: (color) => set({ color }),
  setSize: (size) => set({ size }),

  addStroke: (stroke) => {
    const s = get();
    const page = currentPage(s);
    set(editPage(s, page.strokes.concat([stroke]), page.texts, []));
  },

  addText: (text) => {
    const s = get();
    const page = currentPage(s);
    set(editPage(s, page.strokes, page.texts.concat([text]), []));
  },

  replaceText: (text) => {
    const s = get();
    const page = currentPage(s);
    const texts = page.texts.slice();
    let found = false;
    for (let i = 0; i < texts.length; i++) {
      if (texts[i].id === text.id) {
        texts[i] = text;
        found = true;
        break;
      }
    }
    if (!found) return;
    set(editPage(s, page.strokes, texts, s.selection));
  },

  eraseObject: (id) => {
    const s = get();
    const page = currentPage(s);
    const strokes = page.strokes.filter((stroke) => stroke.id !== id);
    const texts = page.texts.filter((t) => t.id !== id);
    if (strokes.length === page.strokes.length && texts.length === page.texts.length) return;
    set(editPage(s, strokes, texts, []));
  },

  setSelection: (ids) => set({ selection: ids, revision: get().revision + 1 }),

  clearSelection: () => {
    const s = get();
    if (s.selection.length === 0) return;
    set({ selection: [], revision: s.revision + 1 });
  },

  deleteSelection: () => {
    const s = get();
    if (s.selection.length === 0) return;
    const page = currentPage(s);
    const set_ = idSet(s.selection);
    set(
      editPage(
        s,
        page.strokes.filter((stroke) => !set_[stroke.id]),
        page.texts.filter((t) => !set_[t.id]),
        [],
      ),
    );
  },

  moveSelection: (dx, dy) => {
    const s = get();
    if (s.selection.length === 0 || (dx === 0 && dy === 0)) return;
    const page = currentPage(s);
    const set_ = idSet(s.selection);
    set(
      editPage(
        s,
        page.strokes.map((stroke) => (set_[stroke.id] ? translateStroke(stroke, dx, dy) : stroke)),
        page.texts.map((t) => (set_[t.id] ? translateText(t, dx, dy) : t)),
        s.selection,
      ),
    );
  },

  clearPage: () => {
    const s = get();
    const page = currentPage(s);
    if (page.strokes.length === 0 && page.texts.length === 0) return;
    set(editPage(s, [], [], []));
  },

  goToPage: (index) => {
    const s = get();
    if (index < 0 || index >= s.pages.length || index === s.pageIndex) return;
    set({ pageIndex: index, selection: [], revision: s.revision + 1 });
  },

  prevPage: () => {
    const s = get();
    if (s.pageIndex > 0) s.goToPage(s.pageIndex - 1);
  },

  // Next on the last page appends a blank one - the frame has no separate "+" affordance.
  nextPage: () => {
    const s = get();
    if (s.pageIndex < s.pages.length - 1) {
      s.goToPage(s.pageIndex + 1);
      return;
    }
    if (s.pageLimit !== null) return;
    set({
      pages: s.pages.concat([createPage()]),
      histories: s.histories.concat([emptyHistory()]),
      pageIndex: s.pageIndex + 1,
      selection: [],
      revision: s.revision + 1,
    });
  },

  loadPages: (pages, pageIndex = 0) => {
    if (!pages.length) return;
    const safeIndex = Math.max(0, Math.min(pages.length - 1, pageIndex));
    set({
      pages,
      pageIndex: safeIndex,
      histories: pages.map(emptyHistory),
      selection: [],
      revision: get().revision + 1,
    });
  },

  configurePageCount: (count) => {
    const s = get();
    if (count === null) {
      set({ pageLimit: null });
      return;
    }
    const safeCount = Math.max(1, Math.floor(count));
    const pages = s.pages.slice(0, safeCount);
    const histories = s.histories.slice(0, safeCount);
    while (pages.length < safeCount) {
      pages.push(createPage());
      histories.push(emptyHistory());
    }
    const pageIndex = Math.min(s.pageIndex, safeCount - 1);
    set({ pages, histories, pageIndex, pageLimit: safeCount, selection: [], revision: s.revision + 1 });
  },

  undo: () => {
    const s = get();
    const i = s.pageIndex;
    const h = s.histories[i];
    if (h.past.length === 0) return;
    const previous = h.past[h.past.length - 1];
    const cur = s.pages[i];
    const pages = s.pages.slice();
    pages[i] = { id: cur.id, strokes: previous.strokes, texts: previous.texts };
    const histories = s.histories.slice();
    histories[i] = {
      past: h.past.slice(0, h.past.length - 1),
      future: [{ strokes: cur.strokes, texts: cur.texts } as PageContent].concat(h.future),
    };
    set({ pages, histories, selection: [], revision: s.revision + 1 });
  },

  redo: () => {
    const s = get();
    const i = s.pageIndex;
    const h = s.histories[i];
    if (h.future.length === 0) return;
    const next = h.future[0];
    const cur = s.pages[i];
    const pages = s.pages.slice();
    pages[i] = { id: cur.id, strokes: next.strokes, texts: next.texts };
    const histories = s.histories.slice();
    histories[i] = {
      past: pushHistory(h.past, { strokes: cur.strokes, texts: cur.texts }),
      future: h.future.slice(1),
    };
    set({ pages, histories, selection: [], revision: s.revision + 1 });
  },
}));
